import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, P, tokens, useScreenClass } from '@vp/swan';
import * as abReader from '@vp/ab-reader';
import { isCookieConsentPending } from '@vp/cookie-consent-client-reader';

import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from '../../utils/utilities';

import InfoTopBar from '../InfoTopBar';
import { VatContext } from '../../contexts/VatContext';
import { TrackingContext } from '../../hooks/useTracking';
import { getCookie, setCookie } from './GeoIpBar/utils';

const VAT_BAR_EXPERIMENT_NAME = 'fr_vat_panel';
const VAT_BAR_EXPERIMENT_VARIATION_COPY_1 = 'copy_1';
const VAT_BAR_EXPERIMENT_VARIATION_COPY_2 = 'copy_2';
const VAT_BAR_ENABLED_LOCALES = ['fr-FR']
const HAS_SEEN_VAT_BAR_FLAG = "hasSeenVatSelectionBar";
const VAT_BAR_LOCAL_STORAGE_FLAG_TTL_IN_DAYS = 30;
export const VAT_BAR_LOCAL_STORAGE_FLAG = 'hideVatSelectionBar';

// -- todo : temporary code, to be removed after ABC test is done -- //
const vatSelectionBarCopy : any = {
  copy_1 : {
    titleText : "Nos prix sont affichés en HT (TVA de 20 % non incluse). Vous êtes un particulier ?",
    mobilePromptText: "Vous pouvez modifier cette option dans le menu.",
    vatIncText : "Oui, passer en TTC",
    vatExcText : "Non, rester en HT",
  }, 
  copy_2 : {
    titleText : "Tous nos prix sont affichés en HT. Voulez-vous inclure la TVA de 20 % ?",
    mobilePromptText: "Vous pouvez modifier cette option dans le menu.",
    vatIncText : "Oui, passer en TTC",
    vatExcText : "Non, rester en HT",
  }, 
}
// -- todo : temporary code, to be removed after ABC test is done -- //

const VatSelectionBar = ({ locale }: { locale: string }) => {
  const [showVatSelectionBar, setShowVatSelectionBar] = useState(false);
  const [showVatSelectionPromptMobile, setShowVatSelectionPromptMobile] =
    useState(false);
  const [
    vatSelectionBarExperimentVariation,
    setVatSelectionBarExperimentVariation,
  ] = useState<string>('');

  const { vatInclusive, setVatInclusivity } = useContext(VatContext);
  const { trackFlyOutClicked, trackFlyOutViewed, pageSection } =
    useContext(TrackingContext);

  const screenClass = useScreenClass();
  const isMobile = screenClass === 'xs' || screenClass === 'sm';

  useEffect(() => {
    abReader.whenAvailable(() => {
      const variationKey = abReader.getVariation(VAT_BAR_EXPERIMENT_NAME);

      if(variationKey) {
        setVatSelectionBarExperimentVariation(variationKey);
      }
    }, 2000);
  }, []);

  const invalidPageSectionsForVatBar = ['Gallery', 'Cart', 'undefined'];
  const isPageSectionValidForVatSelector = !invalidPageSectionsForVatBar.includes(pageSection) && !!pageSection;
  
  useEffect(() => {
    const isValidExperiemntVariation =
      vatSelectionBarExperimentVariation === VAT_BAR_EXPERIMENT_VARIATION_COPY_1 ||
      vatSelectionBarExperimentVariation === VAT_BAR_EXPERIMENT_VARIATION_COPY_2;
    
    const vatSelectionBarEnabled =
      VAT_BAR_ENABLED_LOCALES.includes(locale) &&
      vatInclusive !== undefined &&
      !vatInclusive &&
      !getLocalStorageWithExpiry(VAT_BAR_LOCAL_STORAGE_FLAG) &&
      !getCookie(HAS_SEEN_VAT_BAR_FLAG) &&
      isPageSectionValidForVatSelector &&
      isValidExperiemntVariation;
      
    setShowVatSelectionBar(vatSelectionBarEnabled)
  }, [locale, vatInclusive, isPageSectionValidForVatSelector, vatSelectionBarExperimentVariation]);

  const savedTrackFlyOutViewed = useRef(trackFlyOutViewed);
  savedTrackFlyOutViewed.current = trackFlyOutViewed;

  const runRef = useRef(false);

  useEffect(() => {
    if (
      showVatSelectionBar &&
      !runRef.current &&
      isPageSectionValidForVatSelector
    ) {
      savedTrackFlyOutViewed.current('Vat Selector Fly-Out', 'view', locale);
      runRef.current = true;
      if(!isCookieConsentPending()) {
        setCookie(HAS_SEEN_VAT_BAR_FLAG, 'true');
      }
    }
  }, [locale, isPageSectionValidForVatSelector, showVatSelectionBar]);

  useEffect(() => {    
    if(vatSelectionBarExperimentVariation && VAT_BAR_ENABLED_LOCALES.includes(locale) && isPageSectionValidForVatSelector) {
      abReader.fireImpression(VAT_BAR_EXPERIMENT_NAME, vatSelectionBarExperimentVariation)
    }
  }, [vatSelectionBarExperimentVariation, locale, isPageSectionValidForVatSelector])

  const handleVatExcluded = () => {
    setVatInclusivity?.(false);
    setLocalStorageWithExpiry(VAT_BAR_LOCAL_STORAGE_FLAG, true, VAT_BAR_LOCAL_STORAGE_FLAG_TTL_IN_DAYS);
    setShowVatSelectionBar(false);
    if (isMobile) {
      setShowVatSelectionPromptMobile(true);
    }
    trackFlyOutClicked('Vat Selector Fly-Out', 'VATex');
  };

  const handleVatIncluded = () => {
    setVatInclusivity?.(true);
    setLocalStorageWithExpiry(VAT_BAR_LOCAL_STORAGE_FLAG, true, VAT_BAR_LOCAL_STORAGE_FLAG_TTL_IN_DAYS);
    setShowVatSelectionBar(false);
    if (isMobile) {
      setShowVatSelectionPromptMobile(true);
    }
    trackFlyOutClicked('Vat Selector Fly-Out', 'VATinc');
  };

  const titleHTML = (
    <P m={0}>{vatSelectionBarCopy[vatSelectionBarExperimentVariation]?.titleText}</P>
  );

  const mobileVatPrompt = (
    <P m={0}>{vatSelectionBarCopy[vatSelectionBarExperimentVariation]?.mobilePromptText}</P>
  );

  const buttonHTML = (
    <>
      <Button
        data-testid="vat-exlusive-button"
        paddingY="3"
        width={screenClass === 'xs' ? 'full-width' : 'standard'}
        bgc={'standard'}
        textColor={'standard'}
        onClick={handleVatExcluded}
      >
        {vatSelectionBarCopy[vatSelectionBarExperimentVariation]?.vatExcText}
      </Button>

      <Button
        data-testid="vat-inclusive-button"
        paddingY="3"
        width={screenClass === 'xs' ? 'full-width' : 'standard'}
        bgc={'standard'}
        textColor={'standard'}
        onClick={handleVatIncluded}
      >
        {vatSelectionBarCopy[vatSelectionBarExperimentVariation]?.vatIncText}
      </Button>
    </>
  );

  return (
    <>
      {showVatSelectionBar && (
        <InfoTopBar
          id="vat-selection-bar"
          title={titleHTML}
          buttons={buttonHTML}
          style={{
            position: 'relative',
            top: tokens.SwanBaseSpace0
          }}
          isVatSelectionBar
        />
      )}

      {showVatSelectionPromptMobile && (
        <InfoTopBar
          id="vat-selection-prompt"
          title={mobileVatPrompt}
          style={{
            position: 'relative',
            top: tokens.SwanBaseSpace0
          }}
          isVatSelectionBar
        />
      )}
    </>
  );
};

export default VatSelectionBar;
